import {
  Col,
  Row,
  DatePicker,
  Table,
  Space,
  Input,
  Button,
  Tooltip,
} from 'antd';
import { FormButton } from 'common/components';
import { useState, useEffect, useMemo } from 'react';
import {
  SearchOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import './TraceableComment.less';
import moment from 'moment';

export const TraceableCommentGridHeader = ({
  children,
  dateRange,
  onDateRangeChange,
  onSearch,
  disabled,
}) => {
  return (
    <Row gutter={4} align='middle' className='traceable-comment-grid-header'>
      <Col flex='350px'>
        <DatePicker.RangePicker
          value={dateRange}
          onChange={onDateRangeChange}
          style={{ width: '100%' }}
          disabled={disabled}
        />
      </Col>

      <Col flex='auto'>
        <Space>
          <FormButton
            icon={<SearchOutlined />}
            type='secondary'
            text='Search'
            onClick={onSearch}
            disabled={disabled}
          />
          {children}
        </Space>
      </Col>
    </Row>
  );
};

export const TraceableCommentTable = ({
  data,
  setLocalComments,
  qaSpecStatus,
  isQAUser,
  isMemberSupplierUser,
  isSuperAdmin,
  onSaveComment,
  disableActions = false,
}) => {
  const [editingRow, setEditingRow] = useState(null);
  const [comments, setComments] = useState([]);
  const uniqueId = useMemo(
    () => Date.now() + Math.floor(Math.random() * 1000),
    []
  );

  useEffect(() => {
    setComments(data.length > 0 ? data : [{ id: uniqueId, isNew: true }]);
  }, [data]);

  const handleEdit = (id) => setEditingRow(id);

  const handleCancel = () => {
    setEditingRow(null);
    setComments(data.length > 0 ? data : [{ id: uniqueId, isNew: true }]);
    setLocalComments(data.length > 0 ? data : [{ id: uniqueId, isNew: true }]);
  };

  const handleChange = (id, field, value) => {
    setComments((prev) =>
      prev.map((comment) =>
        comment.id === id ? { ...comment, [field]: value } : comment
      )
    );
  };

  const handleSave = (id) => {
    const updatedComment = comments.find((comment) => comment.id === id);

    if (
      isMemberSupplierUser &&
      (!updatedComment?.supplierComment ||
        updatedComment.supplierComment.trim() === '')
    ) {
      return;
    }

    if (
      isQAUser &&
      !updatedComment.supplierComment &&
      qaSpecStatus !== 'Submitted'
    ) {
      return;
    }

    if (!updatedComment.qaComment && !updatedComment.supplierComment) {
      setComments((prev) => prev.filter((comment) => comment.id !== id));
      setLocalComments((prev) => prev.filter((comment) => comment.id !== id));
    } else {
      if (onSaveComment) {
        onSaveComment(updatedComment);
      }
      setEditingRow(null);
    }
  };

  const newestRowId = comments.length > 0 ? comments[0].id : null;

  const columns = [
    {
      title: 'QA Spec',
      dataIndex: 'qaSpecName',
      key: 'qaSpecName',
      width: 200,
      render: (text) => <div className='traceable-comment-display'>{text}</div>,
    },
    {
      title: 'Field Name',
      dataIndex: 'qaSpecFieldName',
      key: 'qaSpecFieldName',
      width: 200,
      render: (text) => <div className='traceable-comment-display'>{text}</div>,
    },
    {
      title: 'Supplier Comment',
      dataIndex: 'supplierComment',
      key: 'supplierComment',
      width: 200,
      ellipsis: false,
      render: (text, record) =>
        editingRow === record.id ? (
          <Input.TextArea
            className='traceable-comment-text'
            value={text}
            onChange={(e) =>
              handleChange(record.id, 'supplierComment', e.target.value)
            }
            disabled={isSuperAdmin || !isMemberSupplierUser}
          />
        ) : (
          <Tooltip title={text}>
            <div className='traceable-comment-display'>{text || ''}</div>
          </Tooltip>
        ),
    },
    {
      title: 'Updated By',
      dataIndex: 'supplierName',
      key: 'updatedBySupplier',
      width: 150,
      render: (text) => <div className='traceable-comment-display'>{text}</div>,
    },
    {
      title: 'Last Updated',
      dataIndex: 'supplierLastUpdated',
      key: 'lastUpdatedBySupplier',
      width: 150,
      render: (text) => (text ? moment(text).format('YYYY-MM-DD HH:mm') : ''),
    },
    {
      title: 'QA Comment',
      dataIndex: 'qaComment',
      key: 'qaComment',
      width: 200,
      ellipsis: false,
      render: (text, record) =>
        editingRow === record.id ? (
          <Input.TextArea
            className='traceable-comment-text'
            value={text}
            onChange={(e) =>
              handleChange(record.id, 'qaComment', e.target.value)
            }
            disabled={isSuperAdmin || !isQAUser}
          />
        ) : (
          <Tooltip title={text}>
            <div className='traceable-comment-display'>{text || ''}</div>
          </Tooltip>
        ),
    },
    {
      title: 'Updated By QA',
      dataIndex: 'qaName',
      key: 'updatedByQa',
      width: 150,
      render: (text) => <div className='traceable-comment-display'>{text}</div>,
    },
    {
      title: 'Last Updated QA',
      dataIndex: 'qaLastUpdated',
      key: 'lastUpdatedByQa',
      width: 150,
      render: (text) => (text ? moment(text).format('YYYY-MM-DD HH:mm') : ''),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        if (disableActions) return null;
        if (record.productStatus === 'Closed') return null;

        const isNewestRow = record.id === newestRowId;
        const isSupplierCommentEmpty =
          !record.supplierComment || record.supplierComment.trim() === '';
        const isEditingThisRow = editingRow === record.id;

        const canSupplierAddComment =
          isMemberSupplierUser &&
          isNewestRow &&
          (isSupplierCommentEmpty || isEditingThisRow);

        const canQAEditComment =
          (isQAUser &&
            !isSuperAdmin &&
            isNewestRow &&
            (record.qaComment !== undefined || qaSpecStatus === 'Submitted')) ||
          isEditingThisRow;

        if (!(canSupplierAddComment || canQAEditComment)) return null;

        return isEditingThisRow ? (
          <Space>
            <Button
              icon={<SaveOutlined />}
              onClick={() => handleSave(record.id)}
              disabled={
                isMemberSupplierUser &&
                (!record.supplierComment ||
                  record.supplierComment.trim() === '')
              }
            />
            <Button icon={<CloseOutlined />} onClick={handleCancel} />
          </Space>
        ) : (
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.id)}
            disabled={isSuperAdmin}
          />
        );
      },
    },
  ];

  return (
    <div className='traceable-comment-table-wrapper'>
      <Table
        dataSource={comments}
        columns={columns}
        rowKey='id'
        bordered
        pagination={{ pageSize: 3, position: ['bottomLeft'] }}
      />
    </div>
  );
};
